import { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  useGetQuestionList,
  useGetPreviousAnswers,
} from '@viome/viome-base/src/usecase/Questionnaire/Questionnaire.query';
import {
  Question,
  QuestionnaireFormData,
} from '@viome/viome-base/src/usecase/Questionnaire/Questionnaire.model';
import { useQueryClient } from '@tanstack/react-query';
import { QUERY_KEYS } from '@viome/viome-base/src/api/config';
import { NavBar } from '@/components/design-system/NavBar';
import { QuestionRenderer } from './QuestionRenderer';
import { useQuestionnaireForm } from '@viome/viome-base/src/hooks/useQuestionnaireForm';
import { useQuestionnairePagination } from '@viome/viome-base/src/hooks/useQuestionnairePagination';
import { Button } from '@design-system/Button.tsx';
import { QuestionnaireLoadingSkeleton } from './QuestionnaireLoadingSkeleton';
import { ErrorLoadingPage } from '@screens/ErrorHandling/ErrorLoadingPage';
import { useQuestionnaireProcessed } from '@hooks/useQuestionnaireProcessed';
import { useQuestionnaireSubmission } from '@/hooks/useQuestionnaireSubmission';
import { FormProvider } from 'react-hook-form';
import dayjs from 'dayjs';
import { DOBConfirmationDialog } from './question-types/DOBConfirmationDialog';

export const QuestionnaireQuestionsPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const currentQuestionnaire = location.state?.questionnaire;
  const queryClient = useQueryClient();
  const {
    isQuestionnaireProcessed,
    fromCollectSample,
    confirmedDOBDate,
    setConfirmedDOBDate,
  } = useQuestionnaireProcessed();
  const returnNavigate = fromCollectSample ? '/' : '/questionnaires';

  useEffect(() => {
    document.body.classList.add('questionnaire-active');
    return () => document.body.classList.remove('questionnaire-active');
  }, []);

  // DOB confirmation state
  const [showDOBConfirmation, setShowDOBConfirmation] = useState(false);
  const [dobConfirmationYear, setDOBConfirmationYear] = useState<number>(0);
  const [blockContinue, setBlockContinue] = useState(false);

  const queryParam = fromCollectSample
    ? {
        onboarding_questions_only: true,
      }
    : { question_group_version_id: currentQuestionnaire?.groupId };

  const {
    data: questionnaireData,
    isLoading: isQuestionListLoading,
    isError: isQuestionListError,
  } = useGetQuestionList(queryParam);

  const isReadOnlyMode = fromCollectSample
    ? isQuestionnaireProcessed
    : currentQuestionnaire?.enable_read_only_mode || false;

  const questions = questionnaireData?.questions;
  const parentQuestionIds = questionnaireData?.parentQuestionIds || [];

  const { data: previousAnswers, error: previousAnswersError } =
    useGetPreviousAnswers(parentQuestionIds || []);

  const {
    methods,
    visibleQuestions,
    watchedValues,
    filterFormData,
    formState,
  } = useQuestionnaireForm({
    questions,
    previousAnswers: previousAnswers || {},
  });

  const { isDirty, errors } = formState;
  const { handleSubmit, getValues } = methods;

  const {
    currentQuestions,
    progress,
    isLastPage,
    isCurrentPageValid,
    canGoBack,
    handlePrevious,
    handleNext,
    currentPage,
    totalPages,
  } = useQuestionnairePagination({
    visibleQuestions,
    watchedValues,
    formErrors: errors,
  });

  // Watch for DOB changes to reset blockContinue and confirmed date
  const dobValue = methods.watch('1');
  useEffect(() => {
    if (dobValue) {
      const selectedYear = dayjs(dobValue).year();
      const currentYear = dayjs().year();

      // If user changed their DOB to a different year, unblock continue
      if (blockContinue && selectedYear !== currentYear) {
        setBlockContinue(false);
      }

      // If user changed the date, reset the confirmed date so dialog can show again
      const dobDateString = typeof dobValue === 'string'
        ? dobValue
        : dayjs(dobValue).format('YYYY-MM-DD');
      if (confirmedDOBDate && dobDateString !== confirmedDOBDate) {
        setConfirmedDOBDate(null);
      }
    }
  }, [dobValue, blockContinue, confirmedDOBDate]);

  // Reset blockContinue when navigating away from question 1
  useEffect(() => {
    const hasQuestion1 = currentQuestions.some((q: Question) => q.id === 1);
    if (!hasQuestion1 && blockContinue) {
      setBlockContinue(false);
    }
  }, [currentQuestions, blockContinue]);

  const { handleSubmission, isSubmitting, isRefetching } =
    useQuestionnaireSubmission({
      filterFormData,
      fromCollectSample,
      questionnaireGroupVersionId: currentQuestionnaire?.groupId,
      methods,
      returnNavigate,
    });

  const handleNextOrSubmit = () => {
    // Only check for DOB confirmation if questionId is exactly 1
    const question1 = currentQuestions.find((q: Question) => q.id === 1);

    if (question1) {
      const dobValue = getValues('1');
      if (dobValue) {
        const selectedYear = dayjs(dobValue).year();
        const currentYear = dayjs().year();

        // Convert dobValue to a consistent string format for comparison
        const dobDateString = typeof dobValue === 'string'
          ? dobValue
          : dayjs(dobValue).format('YYYY-MM-DD');

        // If user selected current year, show confirmation dialog
        // But only if this date hasn't been confirmed yet (i.e., different from the confirmed date)
        // Don't show dialog if user is in read-only mode
        if (
          selectedYear === currentYear &&
          dobDateString !== confirmedDOBDate &&
          !isReadOnlyMode
        ) {
          setDOBConfirmationYear(currentYear);
          setShowDOBConfirmation(true);
          return;
        }
      }
    }

    // Normal flow
    if (isLastPage) {
      if (isReadOnlyMode) {
        navigate(returnNavigate);
      } else {
        handleSubmit((formData: QuestionnaireFormData) => {
          handleSubmission(formData);
        })();
      }
    } else {
      handleNext();
    }
  };

  const handleDOBConfirm = () => {
    setShowDOBConfirmation(false);
    setBlockContinue(false);

    // Store the confirmed DOB date so we don't show the dialog again for the same date
    const dobValue = getValues('1');
    if (dobValue) {
      const dobDateString = typeof dobValue === 'string'
        ? dobValue
        : dayjs(dobValue).format('YYYY-MM-DD');
      setConfirmedDOBDate(dobDateString);
    }

    // Continue with normal flow
    if (isLastPage) {
      if (isReadOnlyMode) {
        navigate(returnNavigate);
      } else {
        handleSubmit((formData: QuestionnaireFormData) => {
          handleSubmission(formData);
        })();
      }
    } else {
      handleNext();
    }
  };

  const handleDOBReject = () => {
    setShowDOBConfirmation(false);
    setBlockContinue(true);
  };

  const handleDOBDialogClose = (open: boolean) => {
    setShowDOBConfirmation(open);
    // If dialog is closed via X button, don't block continue
    if (!open && showDOBConfirmation) {
      setBlockContinue(false);
    }
  };

  const handleSaveAndExit = () => {
    if (isDirty && !isReadOnlyMode) {
      handleSubmission(getValues(), true);
    } else {
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.QUESTIONNAIRE_GROUP_LIST],
      });
      navigate(returnNavigate);
    }
  };

  if (!currentQuestionnaire && !fromCollectSample) {
    return null;
  }

  if (isQuestionListLoading || isRefetching) {
    return <QuestionnaireLoadingSkeleton />;
  }

  if (isQuestionListError || previousAnswersError) {
    return (
      <div className="flex flex-col h-screen">
        <NavBar
          returnButtonTitle={
            isSubmitting ? 'Saving...' : isReadOnlyMode ? 'Exit' : 'Save & Exit'
          }
          onReturnClick={isSubmitting ? undefined : handleSaveAndExit}
          className={`px-4 md:px-8 border-b border-gray-200 ${isSubmitting ? '[&_div:first-child]:cursor-not-allowed [&_button]:opacity-50' : ''}`}
        />
        <ErrorLoadingPage hideNavBar />
      </div>
    );
  }

  if (!questions || questions.length === 0) {
    return null;
  }

  return (
    <FormProvider {...methods}>
      <div className="flex flex-col h-screen">
        <NavBar
          returnButtonTitle={
            isSubmitting ? 'Saving...' : isReadOnlyMode ? 'Exit' : 'Save & Exit'
          }
          onReturnClick={isSubmitting ? undefined : handleSaveAndExit}
          className={`px-4 md:px-8 border-b border-gray-200 ${isSubmitting ? '[&_div:first-child]:cursor-not-allowed [&_button]:opacity-50' : ''}`}
        />

        <div className="flex-1 overflow-hidden">
          <div className="h-full overflow-y-auto px-4 pb-24">
            <div className="text-center mt-8 mb-3">
              <>
                {!fromCollectSample && (
                  <h1 className="typography-h4 text-primary mb-2">
                    {currentQuestionnaire.title}
                  </h1>
                )}
                <p className="text-sm text-tertiary">
                  {currentPage + 1} of {totalPages}
                </p>
              </>
            </div>

            <div className="max-w-[73.375rem] mx-auto md:px-8 lg:px-28">
              <div className="w-full h-1 bg-[#E5E7EB] rounded-full overflow-hidden mb-12">
                <div
                  className="h-full bg-[#10B981] transition-all duration-300 ease-in-out"
                  style={{
                    width: `${progress}%`,
                  }}
                />
              </div>
            </div>

            <div className="max-w-[600px] mx-auto">
              {currentQuestions.map((question: Question) => (
                <QuestionRenderer
                  key={question.id}
                  question={question}
                  control={methods.control}
                  disabled={isReadOnlyMode}
                />
              ))}
            </div>
          </div>
        </div>

        <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 max-md:bottom-16">
          <div className="ml-[15.25rem] max-md:ml-0">
            <div className="w-full mx-auto p-4 md:px-8 lg:px-28 lg:py-6 max-w-[73.375rem]">
              <div className="flex justify-between items-center">
                <Button
                  variant="ghost"
                  onClick={handlePrevious}
                  disabled={!canGoBack || isSubmitting}
                  className="text-primary"
                >
                  Previous step
                </Button>
                <Button
                  onClick={handleNextOrSubmit}
                  disabled={!isCurrentPageValid || isSubmitting || blockContinue}
                  className="bg-black text-white rounded-md px-8 py-2 hover:bg-gray-800"
                >
                  {isLastPage
                    ? isSubmitting
                      ? 'Submitting...'
                      : 'Submit'
                    : isReadOnlyMode
                      ? 'Next'
                      : 'Continue'}
                </Button>
              </div>
            </div>
          </div>
        </div>

        <DOBConfirmationDialog
          open={showDOBConfirmation}
          onOpenChange={handleDOBDialogClose}
          year={dobConfirmationYear}
          onConfirm={handleDOBConfirm}
          onReject={handleDOBReject}
        />
      </div>
    </FormProvider>
  );
};
